import { Divider, LinearProgress } from "@mui/material";
import Button from "components/common/Button";
import Container from "components/common/CustomContainer";
import { filterPage } from "components/common/Filters/FiltersHelper";
import { setPageFilter } from "components/common/Filters/GlobalFilters.slice";
import NoDataBanner from "components/common/NoDataBanner";
import Scroller from "components/common/Scroller/InfiniteScroller";
import Typography from "components/common/Typography";
import { fetchAssignedPlansAsync } from "components/features/dashboard/HotelUserDashboard.slice";
import { getHotelUserDashboardInncode } from "components/features/dashboard/HotelUserDashboardHelper";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { missingIncodeText } from "utils/CommonConstants";
import { formatToDisplayRange } from "utils/CommonFunctions";
import "./AssignedPlans.scss";

export default function AssignedPlans() {
  const { list, totalRecords } = useAppSelector((state) => state.hotelUserDashboardSlice.assignedPlans);
  const dispatch = useAppDispatch();
  const [pageNo, setPageNo] = useState<any>(0);
  const innCode = getHotelUserDashboardInncode();
  const isLeadership = useAppSelector((state) => state.app.loggedInUserDetails.isLeadership);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dateRangeFilter = useAppSelector((state) => state.hotelUserDashboardSlice.dateRangeFilter);

  useEffect(() => {
    if (innCode && innCode !== missingIncodeText) {
      getAssignedPlans(0);
      setPageNo(0);
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }
  }, [innCode, dateRangeFilter]);

  useEffect(() => {
    if (pageNo > 0) getAssignedPlans(pageNo);
  }, [pageNo]);

  const getAssignedPlans = (pageNo: number) => {
    dispatch(
      fetchAssignedPlansAsync({
        payload: {
          innCode,
          ...dateRangeFilter
        },
        page: pageNo
      })
    );
  };

  return (
    <Container className="assigned-plans">
      <Container className="assigned-plans-tracker" scroll="content">
        <div className="header">
          <Typography variant={"h5"} showBold>
            Assigned Plans
          </Typography>
          {isLeadership && (
            <Button
              size="Small"
              label="See Property Overview"
              variant="secondary"
              onClick={() => {
                const params = new URLSearchParams(location.search);
                params.set("parentTab", "Property Overview");
                if (dateRangeFilter?.createdDateTo)
                  dispatch(
                    setPageFilter({
                      pageName: filterPage.individualPropertyPlansDisplay,
                      modifiedFilter: [
                        {
                          name: "Created Date",
                          key: "createdDate",
                          options: [formatToDisplayRange(dateRangeFilter)]
                        }
                      ],
                      isFilterApplied: true
                    })
                  );
                navigate({
                  pathname: location.pathname,
                  search: params.toString()
                });
              }}
            />
          )}
        </div>
        {list?.length ? (
          <div className="assigned-plan-table">
            <div className="assigned-plan-header">
              <Typography
                className="plan-name"
                variant={"body2"}
                color={tokens.Text.General.PrimaryBody}
                children={"Plan Name"}
              ></Typography>
              <Typography
                className="plan-status-header"
                variant={"body2"}
                color={tokens.Text.General.PrimaryBody}
                children={"Status"}
              ></Typography>
            </div>
            <Divider />
            <div id="assigned-plan-scroll" className="assigned-plan-scroll" ref={scrollRef}>
              <Scroller
                dataLength={list?.length}
                loadMore={() => {
                  setPageNo(pageNo + 1);
                }}
                totalList={totalRecords}
                label="Plans"
                scrollableTarget={"assigned-plan-scroll"}
              >
                {list?.map((plan: any) => (
                  <div
                    className="assigned-plan-row"
                    key={plan.id}
                    onClick={() => navigate(`/myplandetails/${plan.id}`)}
                  >
                    <Typography
                      showBold
                      className="plan-name"
                      variant={"body2"}
                      color={tokens.Text.General.PrimaryBody}
                    >
                      {plan.title}
                    </Typography>
                    <div className="plan-status">
                      <Typography
                        showBold
                        className="status-text"
                        variant={"label"}
                        color={plan?.isOverdue ? tokens.Text.Feedback.OverdueLight : tokens.Text.General.Secondary}
                      >
                        {plan?.isOverdue ? "Overdue" : plan.status}
                      </Typography>
                      <LinearProgress
                        className="linear-progress"
                        variant="determinate"
                        value={plan.totalCards > 0 ? (plan.cardsCompleted / plan.totalCards) * 100 : 0}
                      />

                      <Typography className="status-subtext" variant={"label"} color={tokens.Text.General.Tertiary}>
                        {`${plan.cardsCompleted}/${plan.totalCards} done`}
                      </Typography>
                    </div>
                  </div>
                ))}
              </Scroller>
            </div>
          </div>
        ) : (
          <NoDataBanner />
        )}
      </Container>
    </Container>
  );
}
